import { Directive, AfterViewInit, OnDestroy, ElementRef, Optional, Host, Renderer2 } from '@angular/core';
import { Dropdown } from 'primeng/dropdown';
import { MultiSelect } from 'primeng/multiselect';

/**
 * Directive pour maintenir une position fixe des dropdowns PrimeNG avec search input.
 *
 * La position initiale (au-dessus ou en-dessous) est déterminée au premier affichage
 * et reste fixe pendant toute la durée d'ouverture du dropdown, même si le nombre
 * de résultats de recherche diminue.
 */
@Directive({
    selector: 'p-dropdown[filter]:not([appendTo="body"]), p-multiSelect[filter]:not([appendTo="body"])',
    standalone: false
})
export class FixedDropdownPositionDirective implements AfterViewInit, OnDestroy {
    private initialPosition: { left: number; top: number; bottom: number; height: number; isAbove: boolean; } | null = null;
    private currentOverlay: HTMLElement | null = null;
    private captureTimeout: any = null;
    private triggerElement: HTMLElement | null = null;
    private scrollTimeout: any = null;
    private isScrolling = false;
    private scrollListener: ((event: Event) => void) | null = null;

    constructor(
        private elementRef: ElementRef,
        private renderer: Renderer2,
        @Optional() @Host() private dropdown?: Dropdown,
        @Optional() @Host() private multiSelect?: MultiSelect
    ) {}

    ngAfterViewInit(): void {
        if (!this.dropdown && !this.multiSelect) {
            return;
        }

        // Écouter les événements d'ouverture/fermeture selon le type de composant
        if (this.dropdown) {
            this.dropdown.onShow.subscribe(() => this.onDropdownShow());
            this.dropdown.onHide.subscribe(() => this.onDropdownHide());
        } else if (this.multiSelect) {
            this.multiSelect.onPanelShow.subscribe(() => this.onDropdownShow());
            this.multiSelect.onPanelHide.subscribe(() => this.onDropdownHide());
        }
    }

    private onDropdownShow(): void {
        // Réinitialiser
        this.initialPosition = null;
        this.currentOverlay = null;

        // Attendre que le panel soit rendu et positionné par PrimeNG
        this.captureTimeout = setTimeout(() => {
            this.captureInitialPosition();
            this.attachScrollListener();
        }, 200);
    }

    private onDropdownHide(): void {
        // Réinitialiser les styles de l'overlay pour éviter l'animation bizarre
        if (this.currentOverlay) {
            this.currentOverlay.style.position = '';
            this.currentOverlay.style.left = '';
            this.currentOverlay.style.top = '';
            this.currentOverlay.style.bottom = '';
            this.currentOverlay.style.transform = '';
            this.currentOverlay.style.willChange = '';
        }

        // Nettoyer tous les timers
        if (this.scrollTimeout) {
            clearTimeout(this.scrollTimeout);
            this.scrollTimeout = null;
        }
        if (this.captureTimeout) {
            clearTimeout(this.captureTimeout);
            this.captureTimeout = null;
        }
        this.detachScrollListener();
        this.initialPosition = null;
        this.currentOverlay = null;
        this.isScrolling = false;
    }

    private captureInitialPosition(): void {
        const overlay = this.findOverlayElement();
        if (!overlay) {
            return;
        }

        this.currentOverlay = overlay;

        // Trouver l'élément déclencheur réel
        this.triggerElement = this.findTriggerElement();

        if (!this.triggerElement) {
            return;
        }

        // Récupérer la position de l'overlay et du trigger
        const overlayRect = overlay.getBoundingClientRect();
        const triggerRect = this.triggerElement.getBoundingClientRect();

        // Déterminer si le dropdown est au-dessus ou en-dessous
        // Si le haut de l'overlay est plus haut que le haut du trigger, c'est au-dessus
        const isAbove = overlayRect.top < triggerRect.top;

        this.initialPosition = {
            left: overlayRect.left,
            top: overlayRect.top,
            bottom: overlayRect.bottom,
            height: overlayRect.height,
            isAbove: isAbove
        };

        // Forcer la position en utilisant position fixed
        overlay.style.position = 'fixed';
        overlay.style.left = overlayRect.left + 'px';
        overlay.style.transform = 'none';
        overlay.style.willChange = 'auto';

        // Si au-dessus, fixer le bottom, sinon fixer le top
        if (isAbove) {
            overlay.style.bottom = (window.innerHeight - overlayRect.bottom) + 'px';
            overlay.style.top = 'auto';
        } else {
            overlay.style.top = overlayRect.top + 'px';
            overlay.style.bottom = 'auto';
        }
    }

    private attachScrollListener(): void {
        this.scrollListener = (event: Event) => {
            // Ne pas interférer si l'événement vient de l'intérieur du dropdown
            if (this.currentOverlay && event.target instanceof Node && this.currentOverlay.contains(event.target)) {
                return;
            }

            // Indiquer qu'on est en train de scroller
            this.isScrolling = true;

            // Mettre à jour
            this.updateOverlayPosition();

            // Nettoyer le timeout précédent
            if (this.scrollTimeout) {
                clearTimeout(this.scrollTimeout);
            }

            // Arrêter le flag de scroll après 150ms sans scroll
            this.scrollTimeout = setTimeout(() => {
                this.isScrolling = false;
            }, 150);
        };
        window.addEventListener('scroll', this.scrollListener, true);
    }

    private detachScrollListener(): void {
        if (this.scrollListener) {
            window.removeEventListener('scroll', this.scrollListener, true);
            this.scrollListener = null;
        }
    }

    private updateOverlayPosition(): void {
        if (!this.currentOverlay || !this.triggerElement || !this.initialPosition) {
            return;
        }

        // Récup la nouvelle position du trigger
        const triggerRect = this.triggerElement.getBoundingClientRect();

        // Éviter de modifier si on est en train de faire autre chose que scroller
        // (par exemple si l'utilisateur tape dans le champ de recherche)
        const overlayRect = this.currentOverlay.getBoundingClientRect();
        const isPositionStillValid = this.initialPosition.isAbove
            ? Math.abs(overlayRect.bottom - this.initialPosition.bottom) < 100
            : Math.abs(overlayRect.top - this.initialPosition.top) < 100;

        // Si la position n'a pas trop changé, ne pas toucher (l'utilisateur est peut-être en train de filtrer)
        if (isPositionStillValid && !this.isScrolling) {
            return;
        }

        // Mettre à jour la position de l'overlay
        this.currentOverlay.style.left = triggerRect.left + 'px';

        if (this.initialPosition.isAbove) {
            // Si au-dessus, positionner en utilisant bottom
            this.currentOverlay.style.bottom = (window.innerHeight - triggerRect.top) + 'px';
            this.currentOverlay.style.top = 'auto';
        } else {
            // Si en-dessous, positionner en utilisant top
            this.currentOverlay.style.top = triggerRect.bottom + 'px';
            this.currentOverlay.style.bottom = 'auto';
        }
    }

    private findTriggerElement(): HTMLElement | null {
        if (this.dropdown) {
            const container = this.elementRef.nativeElement.querySelector('.p-dropdown');
            if (container) {
                return container;
            }
        }
        if (this.multiSelect) {
            const container = this.elementRef.nativeElement.querySelector('.p-multiselect');
            if (container) {
                return container;
            }
        }
        return this.elementRef.nativeElement;
    }

    private findOverlayElement(): HTMLElement | null {
        if (this.currentOverlay) {
            const style = window.getComputedStyle(this.currentOverlay);
            if (style.display !== 'none' && style.visibility !== 'hidden') {
                return this.currentOverlay;
            }
        }
        const dropdownPanels = Array.from(document.querySelectorAll('.p-dropdown-panel')) as HTMLElement[];
        const multiSelectPanels = Array.from(document.querySelectorAll('.p-multiselect-panel')) as HTMLElement[];

        const allPanels = [...dropdownPanels, ...multiSelectPanels];

        const visiblePanels = allPanels.filter(panel => {
            const style = window.getComputedStyle(panel);
            return style.display !== 'none' && style.visibility !== 'hidden';
        });

        return visiblePanels.length > 0 ? visiblePanels[visiblePanels.length - 1] : null;
    }

    ngOnDestroy(): void {
        if (this.scrollTimeout) {
            clearTimeout(this.scrollTimeout);
        }
        if (this.captureTimeout) {
            clearTimeout(this.captureTimeout);
        }
        this.detachScrollListener();
    }
}
